// Ficha 07 — Registrar el enganche (pista SOFOM · crédito). Look 1:1 con el mock
// `wizard/07-enganche.html`: head con cuota semanal y chip de nivel, hero navy con el MONTO A
// COBRAR (= enganche del crédito vía contract/preview + líneas de contado) + folio ENG,
// y hasta 3 formas de pago con la lista compartida del cobro de contado.
//
// INTEGRADA (guía §07 + contrato real): `POST …/down-payment` — regla dura `amountCents ==
// enganche + líneas de contado` (PAYMENT_AMOUNT_MISMATCH si cambió); idempotente
// por sesión (mismo cobro re-confirma; distinto → PAYMENT_ALREADY_REGISTERED). El folio
// `SK-…-ENG-…` lo genera el SERVER dentro del endpoint — NO llamar `/payment/folio` aquí (es del
// contado y produce otro formato), por eso antes de cobrar se muestra "se genera al confirmar".
// Al confirmar se marca la condición 1 del checklist de activación (el dinero va a la caja del
// retailer, el motor solo recibe la marca).
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, Check } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { ApiError } from '@/api/errors';
import { useContractPreview, useDownPayment, useWizardSession } from '../api/useWizard';
import { useCatalogLineInfo } from '../api/useCatalogLineInfo';
import { useChargeableLines } from '../api/useChargeableLines';
import { wizardErrorMsg } from '../api/wizardError';
import { chargeFailure } from '../chargeErrors';
import { chargeLinesOf, downPaymentChargeCents } from '../saleCharge';
import { UnchargeableLinesNotice } from '../components/UnchargeableLinesNotice';
import { ChargeReloadNotice } from '../components/ChargeReloadNotice';
import { PromotionRevalidationNotice } from '../components/PromotionRevalidationNotice';
import { pendingRevalidationLineIds } from '../promotionRevalidation';
import { LevelChip } from '../components/LevelChip';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { NO_CHARGE_LABEL, registeredMethodsLabel } from '../payments';
import { PaymentListForm } from '../components/PaymentListForm';
import {
    balanceBlocker,
    initialPaymentDrafts,
    paymentsBalance,
    paymentsBlocker,
    toSalePayments,
    type PaymentDraft,
} from '../salePayments';
import { PaymentBreakdown } from '@/shared/patterns/SalePayments/PaymentBreakdown';
import type { ActivationChecklist } from '../types';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';

const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

/** Las 4 condiciones de activación, en orden de aparición en el flujo (07 → 08 → 09). */
const CHECKLIST_ITEMS: { key: keyof ActivationChecklist; label: string }[] = [
    { key: 'downPayment', label: 'Enganche cobrado' },
    { key: 'contractSigned', label: 'Contrato firmado' },
    { key: 'imeiEnrolled', label: 'IMEI registrado' },
    { key: 'lockVerified', label: 'Candado activo' },
];

export function DownPaymentStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const sessionQuery = useWizardSession(sessionId);
    const lineInfo = useCatalogLineInfo(sessionId);
    const session = sessionQuery.data;
    const downPayment = useDownPayment(sessionId);
    const chargeable = useChargeableLines(sessionId, session);

    const [drafts, setDrafts] = useState<readonly PaymentDraft[]>(initialPaymentDrafts);
    /** Checklist que devolvió el cobro (solo tras confirmar en esta visita; al reanudar no está). */
    const [checklist, setChecklist] = useState<ActivationChecklist | null>(null);
    /** Rechazo porque la venta cambió: queda en pantalla y frena el cobro hasta recargar. */
    const [reloadMessage, setReloadMessage] = useState<string>();

    const paid = !!session?.payment;
    const creditId = session?.loan?.creditId;
    // El monto del enganche vive en loan-credit (la sesión solo guarda punteros): se rehidrata con
    // contract/preview. Ya cobrado no hace falta — la sesión trae payment.amountCents y el folio.
    const preview = useContractPreview(sessionId, !!session && !paid && !!creditId);
    const credit = preview.data?.credit;

    if (!session) return <Loading size="lg" />;
    const product = session.product;
    const level = session.loan?.level;

    // Sin crédito originado no hay monto que cobrar — la cotización es el paso anterior.
    if (!creditId) {
        return (
            <WizardCard>
                <WizardCardHead eyebrow="Crédito SureKeep" title="Registrar el enganche" />
                <WizardCardBody>
                    <p className="text-[12.5px] text-muted-foreground">
                        Todavía no hay un crédito cotizado. Regresa a Cotización y confírmala.
                    </p>
                </WizardCardBody>
                <WizardFootNav
                    back={
                        <WizardBackLink
                            sessionId={sessionId}
                            step="quote"
                            label="← Volver a la cotización"
                        />
                    }
                />
            </WizardCard>
        );
    }

    const hasLines = session.reservedLines !== undefined;
    const revalidationPending = pendingRevalidationLineIds(session).length > 0;
    const lines = chargeLinesOf(session, { credit: true, ...lineInfo });
    // Enganche del crédito + lo que va de contado, con la misma regla que valida el silo.
    const chargeCents = credit ? downPaymentChargeCents(session, credit.downPaymentCents) : 0;
    const amountCents = paid ? (session.payment?.amountCents ?? 0) : chargeCents;
    const amount = formatMXN(amountCents / 100);
    // Ya cobrado el preview no corre: el reparto congelado de las líneas dice si hubo contado.
    const withCashLines = paid
        ? hasLines && lines.some((l) => (l.cashCents ?? 0) > 0)
        : credit !== undefined && chargeCents > credit.downPaymentCents;

    const valid = paymentsBlocker(drafts, chargeCents) === null;
    const balanceLabel =
        drafts.length > 1 ? balanceBlocker(paymentsBalance(drafts, chargeCents)) : null;

    const confirmar = async (): Promise<void> => {
        if (!credit) return;
        try {
            const r = await downPayment.mutateAsync({
                amountCents: chargeCents,
                payments: toSalePayments(drafts, chargeCents),
            });
            setChecklist(r.checklist);
            toast.success(
                `${withCashLines ? 'Cobrado hoy' : 'Enganche cobrado'} — folio ${r.folio}`,
            );
        } catch (e) {
            const failure = chargeFailure(e);
            if (failure) {
                toast.error(failure.message);
                if (failure.recovery === 'product') {
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'product' },
                    });
                } else if (failure.recovery === 'build-sale') {
                    void sessionQuery.refresh();
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'build-sale' },
                    });
                } else if (failure.recovery === 'quote') {
                    void navigate({
                        to: '/wizard/s/$sessionId/$step',
                        params: { sessionId, step: 'quote' },
                    });
                } else if (failure.recovery === 'reload') {
                    setReloadMessage(failure.message);
                }
                return;
            }
            if (e instanceof ApiError) {
                if (e.code === 'PAYMENT_ALREADY_REGISTERED') {
                    toast.error('Esta venta ya tenía un cobro registrado — se recargó el estado.');
                    void sessionQuery.refresh();
                    return;
                }
            }
            // Cualquier otro error tipado del silo trae msg en español — se muestra ese.
            toast.error(wizardErrorMsg(e, 'No se pudo registrar el cobro — intenta de nuevo.'));
        }
    };

    return (
        <WizardCard>
            <WizardCardHead
                eyebrow="Crédito SureKeep"
                title="Registrar el enganche"
                right={<LevelChip level={level} />}
                sub={
                    product ? (
                        <>
                            Equipo:{' '}
                            <strong className="font-semibold text-foreground">
                                {catalogDisplayName(product, lineInfo.descriptions)}
                            </strong>
                            {credit ? (
                                <>
                                    {' '}
                                    · Cuota semanal:{' '}
                                    <span className="tabular-nums">
                                        {formatMXN(credit.weeklyPaymentWithIvaCents / 100)}
                                    </span>{' '}
                                    × {credit.termWeeks} semanas
                                </>
                            ) : null}
                        </>
                    ) : undefined
                }
            />

            <WizardCardBody className="flex flex-col gap-5">
                {!paid && preview.isPending ? (
                    <Loading size="lg" />
                ) : !paid && preview.isError ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                        <p className="text-[12.5px] text-muted-foreground">
                            {wizardErrorMsg(
                                preview.error,
                                'No se pudo leer el monto del enganche del motor de crédito.',
                            )}
                        </p>
                        <Button
                            size="sm"
                            isLoading={preview.isFetching}
                            onClick={() => void preview.refetch()}
                        >
                            Reintentar
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* .amount-hero — monto del enganche + folio ENG de conciliación. */}
                        <div className="rounded-xl bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(222_47%_20%))] px-[22px] py-5 text-primary-foreground">
                            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] opacity-[0.82]">
                                {paid
                                    ? withCashLines
                                        ? 'Cobrado hoy'
                                        : 'Enganche cobrado'
                                    : 'Monto a cobrar'}
                            </div>
                            <div className="mt-1 text-[34px] font-bold leading-[1.05] tracking-[-0.02em] tabular-nums">
                                {amount}
                            </div>
                            <div className="mt-1 text-[12.5px] opacity-[0.88]">
                                {hasLines
                                    ? withCashLines
                                        ? 'Enganche + contado'
                                        : 'Enganche'
                                    : `Enganche pactado${product ? ` para ${catalogDisplayName(product, lineInfo.descriptions)}` : ''}`}
                            </div>
                            <div className="mt-2 border-t border-primary-foreground/20 pt-2">
                                <div className="text-[9.5px] uppercase tracking-wider opacity-70">
                                    Folio de cobro
                                </div>
                                <div className="font-mono text-[13px] font-semibold tracking-[0.02em]">
                                    {session.folio ?? 'se genera al confirmar el cobro'}
                                </div>
                            </div>
                        </div>

                        {!paid && revalidationPending && (
                            <PromotionRevalidationNotice sessionId={sessionId} />
                        )}

                        {!paid && (
                            <ChargeReloadNotice
                                message={reloadMessage}
                                onReload={async () => {
                                    // El crédito o las líneas cambiaron en otra pantalla: se releen los dos.
                                    await Promise.all([preview.refetch(), sessionQuery.refresh()]);
                                    setReloadMessage(undefined);
                                }}
                            />
                        )}

                        {!paid && (
                            <UnchargeableLinesNotice
                                lines={chargeable}
                                onReload={() => void sessionQuery.refresh()}
                                onFixLost={() => {
                                    void navigate({
                                        to: '/wizard/s/$sessionId/$step',
                                        params: { sessionId, step: 'build-sale' },
                                    });
                                }}
                            />
                        )}

                        {paid ? (
                            /* ── Estado registrado (o reanudación): checklist de activación ── */
                            <div className="rounded-lg border border-border bg-card p-4">
                                <div className="flex items-center gap-2">
                                    <span className="flex size-6 flex-none items-center justify-center rounded-full bg-success/15 text-success">
                                        <Check className="size-3.5" strokeWidth={3} aria-hidden />
                                    </span>
                                    <p className="text-[13px] font-semibold">
                                        Cobro registrado
                                        {session.payment
                                            ? ` — ${registeredMethodsLabel(session.payment) ?? NO_CHARGE_LABEL}`
                                            : ''}
                                    </p>
                                </div>
                                {session.payment?.payments &&
                                    session.payment.payments.length > 1 && (
                                        <PaymentBreakdown
                                            lines={session.payment.payments}
                                            className="mt-3"
                                        />
                                    )}
                                <p className={cn(HINT, 'mt-2')}>
                                    El crédito se activa al terminar la entrega.
                                </p>
                                {checklist && (
                                    <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                                        {CHECKLIST_ITEMS.map((item) => {
                                            const done = checklist[item.key];
                                            return (
                                                <div
                                                    key={item.key}
                                                    className={cn(
                                                        'flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11.5px]',
                                                        done
                                                            ? 'bg-success/10 font-medium text-success'
                                                            : 'bg-muted/50 text-muted-foreground',
                                                    )}
                                                >
                                                    <Check
                                                        className={cn(
                                                            'size-3',
                                                            !done && 'opacity-30',
                                                        )}
                                                        strokeWidth={3}
                                                        aria-hidden
                                                    />
                                                    {item.label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : chargeCents === 0 ? (
                            <p className={HINT}>No hay nada que cobrar: confirma para continuar.</p>
                        ) : (
                            <PaymentListForm
                                drafts={drafts}
                                onChange={setDrafts}
                                totalCents={chargeCents}
                                title={
                                    withCashLines
                                        ? '¿Cómo paga lo de hoy?'
                                        : '¿Cómo va a pagar el enganche?'
                                }
                            />
                        )}
                    </>
                )}
            </WizardCardBody>

            {/* El «Guardar y salir» de aquí solo lanzaba un toast: no guardaba prospecto, no pedía
                motivo y no salía. Ahora es el del pie compartido, que abre el modal de verdad. */}
            {/* Con el enganche ya cobrado no hay regreso: la cotización quedó cerrada con ese cobro. */}
            <WizardFootNav
                back={paid ? undefined : <WizardBackLink sessionId={sessionId} step="quote" />}
            >
                {paid ? (
                    <Button
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() =>
                            void navigate({
                                to: '/wizard/s/$sessionId/$step',
                                params: { sessionId, step: 'sign' },
                            })
                        }
                    >
                        Continuar a la firma
                    </Button>
                ) : (
                    <Button
                        disabled={
                            !valid ||
                            !credit ||
                            chargeable.blocked ||
                            !!reloadMessage ||
                            revalidationPending
                        }
                        isLoading={downPayment.isPending}
                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                        onClick={() => void confirmar()}
                    >
                        {balanceLabel ?? 'Confirmar cobro y continuar'}
                    </Button>
                )}
            </WizardFootNav>
        </WizardCard>
    );
}
